import * as Sentry from '@sentry/nuxt'

export interface RunActionOptions {
  /** Sentry `extra.action` tag and the console.error label — matches the store action's own name. */
  action: string
  /** Extra identifying fields (ids, params) attached to the Sentry report. */
  extra?: Record<string, unknown>
  /** Rethrow after recording (default true). Fire-and-forget loaders that only need `error` set pass false. */
  rethrow?: boolean
}

/**
 * Every store action here used to hand-roll the same catch block: extract a message, stash it on
 * `this.error`, report to Sentry with an `action`/`extra` tag, log it, and rethrow. Centralizing
 * that means the error-reporting shape only needs to change in one place, and a new action can't
 * forget a step (e.g. the Sentry tag) that the others have.
 */
export async function runAction<T>(
  setError: (message: string) => void,
  options: RunActionOptions,
  fn: () => Promise<T>,
): Promise<T | undefined> {
  // Single choke point for every backend-calling store action (getGroup, getGroupUsers,
  // fetchMessages, ...), so "what did we request and when" can be answered by grepping the
  // console for `[store]` instead of instrumenting each action by hand. `startedAt` is logged
  // instead of relying on the console's own timestamp so it survives being copy-pasted out of
  // devtools.
  const startedAt = performance.now()
  console.debug(`[store] ${options.action} start`, { at: new Date().toISOString(), ...options.extra })
  try {
    const result = await fn()
    console.debug(`[store] ${options.action} done in ${(performance.now() - startedAt).toFixed(0)}ms`, options.extra)
    return result
  } catch (e: any) {
    const message = e?.message || String(e)
    setError(message)
    console.error(`[store] ${options.action} failed after ${(performance.now() - startedAt).toFixed(0)}ms:`, e)
    Sentry.captureException(e, { extra: { action: options.action, ...options.extra } })
    if (options.rethrow ?? true) throw e
    return undefined
  }
}
